import React, { useState } from "react";
import dropdownarrow from "../../../images/dropdownarrow.svg";
import darkdropdownarrow from "../../../images/darkdropdownarrow.svg";
import graydropdown from "../../../images/graydropdown.svg";
import smallwhite from "../../../images/smallwhite.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  selectCountries,
  selectIsCountryLoading,
  setSelectedCountryLeague,
} from "../../../store/country-slice";
import AllCountriesSkeleton from "./AllCountryLoadingSkeleton";

const AllCountries = ({ isDarkMode }) => {
  const [isCountryVisible, setIsCountryVisible] = useState(true);
  const isCountryLoading = useSelector(selectIsCountryLoading);
  const [expandedCountries, setExpandedCountries] = useState(new Set());
  const countries = useSelector(selectCountries);
  const dispatch = useDispatch();

  const toggleCountryVisibility = () => {
    setIsCountryVisible(!isCountryVisible);
  };

  const toggleCountryExpansion = (countryIndex) => {
    const newExpandedCountries = new Set(expandedCountries);
    if (newExpandedCountries.has(countryIndex)) {
      newExpandedCountries.delete(countryIndex);
    } else {
      newExpandedCountries.add(countryIndex);
    }
    setExpandedCountries(newExpandedCountries);
  };

  const isCountryExpanded = (countryIndex) => {
    return expandedCountries.has(countryIndex);
  };

  const handleLeagueClick = (country, league) => {
    dispatch(
      setSelectedCountryLeague({
        country: country.country,
        league: league,
      })
    );
  };

  // Function to get country flag URL
  const getFlagUrl = (countryName) => {
    // Handle special cases for country names that don't match standard country names
    const countryMappings = {
      "United States of America": "United States",
      Americas: "United States",
      Europe: null,
      World: null,
    };

    // Check if country has a mapping or should be excluded
    const mappedCountry = countryMappings[countryName];
    if (mappedCountry === null) return null;

    const finalCountryName = mappedCountry || countryName;

    // Use REST Countries API for flag images
    return `https://restcountries.com/v3.1/name/${encodeURIComponent(
      finalCountryName
    )}?fields=flags`;
  };

  // Component for flag image with error handling
  const CountryFlag = ({ countryName, className }) => {
    const [flagUrl, setFlagUrl] = useState(null);
    const [flagError, setFlagError] = useState(false);
    const [flagLoading, setFlagLoading] = useState(true);

    React.useEffect(() => {
      const fetchFlag = async () => {
        const apiUrl = getFlagUrl(countryName);
        if (!apiUrl) {
          setFlagLoading(false);
          return;
        }

        try {
          const response = await fetch(apiUrl);
          if (response.ok) {
            const data = await response.json();
            if (data && data[0] && data[0].flags && data[0].flags.svg) {
              setFlagUrl(data[0].flags.svg);
            }
          }
        } catch (error) {
          setFlagError(true);
        } finally {
          setFlagLoading(false);
        }
      };

      fetchFlag();
    }, [countryName]);

    if (flagLoading) {
      return (
        <div
          className={`${className} bg-gray-200 dark:bg-gray-600 animate-pulse rounded-sm`}
        ></div>
      );
    }

    if (flagError || !flagUrl) {
      // Return a placeholder for countries without flags or on error
      return (
        <div
          className={`${className} bg-gray-300 dark:bg-gray-600 rounded-sm flex items-center justify-center`}
        >
          <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
            {countryName.substring(0, 2).toUpperCase()}
          </span>
        </div>
      );
    }

    return (
      <img
        src={flagUrl}
        alt={`${countryName} flag`}
        className={`${className} object-cover rounded-sm`}
        onError={() => setFlagError(true)}
      />
    );
  };

  if (isCountryLoading) {
    return <AllCountriesSkeleton isDarkMode={isDarkMode} itemCount={8} />;
  }

  return (
    <div className="rounded-[6px] bg-white dark:bg-bluedarkmodecolor shadow-dashboardshadow mt-[12px]">
      <div
        className="dropdown-block cursor-pointer flex items-center justify-between rounded-[6px_6px_0px_0px] p-[8px_10px] xl:p-[8px_15px] bg-darkblue dark:bg-bluedarkmode"
        onClick={toggleCountryVisibility}
      >
        <span className="text-white text-sm text-base s18:text-[18px] font-medium !leading-tight s18:leading-[155%]">
          All countries
        </span>
        <img
          src={isDarkMode ? darkdropdownarrow : dropdownarrow}
          alt="dropdown"
          className={`w-[15px] xl:w-[19px] transition-transform duration-200 ${
            isCountryVisible ? "rotate-180" : ""
          }`}
        />
      </div>

      {isCountryVisible && (
        <div className="country-block">
          {countries.map((country, index) => (
            <div key={index} className="country-item">
              {/* Country Header */}
              <div
                className="cursor-pointer flex justify-between py-2 px-3 s18:p-[10px_14px] border-b-[1px] border-solid border-bordercolor hover:bg-gray-50 dark:hover:bg-darktitlehovercolor transition-colors duration-150"
                onClick={() => toggleCountryExpansion(index)}
              >
                <div className="flex items-center">
                  {/* Country Flag */}
                  <CountryFlag
                    countryName={country.country}
                    className="w-[16px] h-[12px] xl:w-[20px] xl:h-[15px] mr-2 flex-shrink-0"
                  />
                  <p className="country-name text-darkblue dark:text-white text-[12px] xl:text-sm font-medium leading-[128%]">
                    {country.country}
                  </p>
                </div>

                <div className="flex items-center">
                  <span className="text-lightshadeblue dark:text-lightwhite text-[12px] xl:text-sm font-normal leading-[128%] mr-2">
                    {country.leagues_count}
                  </span>
                  {country.leagues_count > 0 && (
                    <img
                      src={isDarkMode ? smallwhite : graydropdown}
                      alt="dropdown"
                      className={`w-[12px] xl:w-[15px] transition-transform duration-200 ${
                        isCountryExpanded(index) ? "rotate-180" : ""
                      }`}
                    />
                  )}
                </div>
              </div>

              {/* Leagues List */}
              {isCountryExpanded(index) &&
                country.leagues &&
                country.leagues.length > 0 && (
                  <div className="leagues-list bg-gray-50 dark:bg-darktitleColor border-b-[1px] border-solid border-bordercolor">
                    {country.leagues.map((league, leagueIndex) => (
                      <div
                        onClick={() => handleLeagueClick(country, league)}
                        key={league.league_id}
                        className="league-item cursor-pointer flex justify-between items-center py-2 px-6 s18:p-[8px_20px] border-b-[1px] border-solid border-gray-200 dark:border-gray-700 last:border-b-0 hover:bg-gray-100 dark:hover:bg-darktitlehovercolor transition-colors duration-150"
                      >
                        <p className="league-name text-darkblue dark:text-white text-[11px] xl:text-[12px] font-medium leading-[128%]">
                          {league.leagueName}
                        </p>

                        <div className="flex items-center">
                          <span className="games-count text-lightshadeblue dark:text-lightwhite text-[11px] xl:text-[12px] font-normal">
                            {league.games_count}
                          </span>
                          {league.games_count > 0 && (
                            <div className="ml-2 w-2 h-2 bg-green-500 rounded-full"></div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

              {/* Empty State for No Leagues */}
              {isCountryExpanded(index) &&
                (!country.leagues || country.leagues.length === 0) && (
                  <div className="empty-leagues bg-gray-50 dark:bg-gray-800 border-b-[1px] border-solid border-bordercolor py-3 px-6">
                    <p className="text-gray-500 dark:text-gray-400 text-[11px] xl:text-[12px] font-normal italic">
                      No leagues available
                    </p>
                  </div>
                )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AllCountries;
